import type { SigningEnvelope } from "../types/esign.types.ts"
import { isCountersigner } from "./countersigner.ts"

export type Headline =
  | { key: "declined"; name: string; reason: string; companyWaiting: false }
  | { key: "everyoneSigned"; companyWaiting: false }
  | { key: "completed"; at: string; companyWaiting: false }
  | { key: "waitingOnCompany"; companyWaiting: true }
  | { key: "waitingOn"; names: string[]; companyWaiting: boolean }
  | {
      key: "waitingOnMore"
      names: [string]
      count: number
      companyWaiting: false
    }
  | {
      key: "waitingOnMoreAndCompany"
      names: [string]
      count: number
      companyWaiting: true
    }

/** One line saying what a sent signature request is waiting on; null when its status says it all. */
export const headlineOf = (envelope: SigningEnvelope): Headline | null => {
  if (envelope.status === "declined") {
    const decliner = envelope.recipients.find(
      (recipient) => recipient.id === envelope.declined?.recipient
    )
    return {
      key: "declined",
      name: decliner?.name ?? "",
      reason: envelope.declined?.reason ?? "",
      companyWaiting: false
    }
  }
  if (envelope.status === "completed" && envelope.seal) {
    return {
      key: "completed",
      at: envelope.seal.timestamp,
      companyWaiting: false
    }
  }
  if (envelope.status === "signed" || envelope.status === "completed") {
    return { key: "everyoneSigned", companyWaiting: false }
  }
  if (envelope.status !== "sent" && envelope.status !== "partially_signed")
    return null

  const waiting = envelope.recipients.filter((recipient) =>
    envelope.waiting_on?.includes(recipient.id)
  )
  const companyWaiting = waiting.some(isCountersigner)
  const names = waiting
    .filter((recipient) => !isCountersigner(recipient))
    .map((recipient) => recipient.name)
  if (names.length === 0) {
    return companyWaiting
      ? { key: "waitingOnCompany", companyWaiting: true }
      : null
  }
  if (names.length > 2) {
    const first: [string] = [names[0]]
    const count = names.length - 1
    return companyWaiting
      ? { key: "waitingOnMoreAndCompany", names: first, count, companyWaiting }
      : { key: "waitingOnMore", names: first, count, companyWaiting }
  }
  return { key: "waitingOn", names, companyWaiting }
}
